import React, { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import { supabase } from '../lib/supabase'
import ReportDocument from '../components/ReportDocument'
import WshcChecklistSection from '../components/WshcChecklistSection'
import { resolveSnapshotMedia } from '../lib/reportAppendix'

// ReportPrint — PRINT-ONLY route consumed by the `render-report-package` background function's headless
// Chromium. It renders ONLY the formal ReportDocument (no app chrome, no ack form) using the EXACT same data
// path as the client acknowledgement page (get_report_for_ack + report-ack-media) and the EXACT same render
// call — so there is zero duplicated layout / no separate template. When data + webfont + all images are
// painted it sets window.__READY__ = true, which the function waits on before calling page.pdf().
//
// A self-hosted webfont is mapped onto the "Arial" family so text renders deterministically on the Lambda
// host (which has no system Arial). ReportDocument's inline `font-family: Arial` therefore resolves to the
// bundled font without any change to ReportDocument itself.
export default function ReportPrint() {
  const { token } = useParams()
  // ?part=front|wshc|tail → multi-pass WSHC-format render (set by the render function per snapshot age).
  // No part → the classic single-pass full document (all pre-cutoff snapshots).
  const part = new URLSearchParams(window.location.search).get('part') || null
  const [status, setStatus] = useState('loading')   // loading | ready | error
  const [doc, setDoc] = useState(null)
  const [logos, setLogos] = useState({})
  const [media, setMedia] = useState({})

  useEffect(() => {
    let cancelled = false
    ;(async () => {
      try {
        const { data: d, error } = await supabase.rpc('get_report_for_ack', { p_token: token })
        if (error || !d || !d.job) { window.__RENDER_ERROR__ = 'no data'; if (!cancelled) setStatus('error'); return }
        // Same media resolver as the client page: the audit-evidence bucket is private, so sign every path via
        // the token-verified report-ack-media edge function.
        const signer = async (p) => {
          try { const { data } = await supabase.functions.invoke('report-ack-media', { body: { token, path: p } }); return data?.url || null } catch (e) { return null }
        }
        const m = await resolveSnapshotMedia((d.snapshot?.snapshot?.evidence) || [], 3600, signer)
        if (cancelled) return
        setDoc({ client: d.client, job: d.job, snapshot: d.snapshot, ack: d.ack || null, currentVersion: d.current_version ?? null, checklistRevision: d.checklist_revision })
        setLogos(d.logos || {})
        setMedia(m)
        setStatus('ready')
      } catch (e) { window.__RENDER_ERROR__ = String(e); if (!cancelled) setStatus('error') }
    })()
    return () => { cancelled = true }
  }, [token])

  // Signal the headless renderer ONLY once everything is painted: React committed, webfont loaded, every <img>
  // finished (loaded or errored). The function waits on window.__READY__ so page.pdf() never fires mid-load.
  useEffect(() => {
    if (status !== 'ready') return
    let cancelled = false
    ;(async () => {
      try { if (document.fonts && document.fonts.ready) await document.fonts.ready } catch (e) {}
      const imgs = Array.from(document.images)
      await Promise.all(imgs.map(img => img.complete ? Promise.resolve() : new Promise(r => { img.onload = img.onerror = r })))
      await new Promise(r => requestAnimationFrame(() => requestAnimationFrame(r)))
      if (!cancelled) window.__READY__ = true
    })()
    return () => { cancelled = true }
  }, [status, media, doc])

  if (status === 'error') return <div id="print-error" style={{ padding: 24, fontFamily: 'Arial' }}>Unable to load report.</div>
  if (status !== 'ready' || !doc) return <div style={{ padding: 24, fontFamily: 'Arial', color: '#888' }}>Preparing…</div>
  return (
    <>
      <style>{`
        @font-face { font-family:'Arial'; font-style:normal; font-weight:400; src:url('/fonts/report-sans.ttf') format('truetype'); font-display:block; }
        @font-face { font-family:'Arial'; font-style:normal; font-weight:700; src:url('/fonts/report-sans-bold.ttf') format('truetype'); font-display:block; }
        html, body { background:#fff !important; margin:0; padding:0; }
        #report-print-root { padding: 0 6px; }
      `}</style>
      <div id="report-print-root">
        {part === 'wshc'
          ? <WshcChecklistSection doc={doc} />
          : <ReportDocument doc={doc} logos={logos} media={media} part={part}
              onDownload={(ev) => { const u = media[ev?.storage_path]; if (u) window.open(u, '_blank') }} />}
      </div>
    </>
  )
}
